import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import logger from '@/utils/logger'

export const useTenantStore = defineStore('tenant', () => {
    // State
    const currentTenant = ref(null)
    const tenantConfig = ref({})
    const branding = ref({})
    const allTenants = ref([])
    const isLoading = ref(false)

    // Getters
    const activeTenants = computed(() =>
        allTenants.value.filter(t => t.status === 'active')
    )

    const suspendedTenants = computed(() =>
        allTenants.value.filter(t => t.status === 'suspended')
    )

    const trialTenants = computed(() =>
        allTenants.value.filter(t => t.subscription_type === 'trial')
    )

    const hasTenant = computed(() => !!currentTenant.value)

    const tenantName = computed(() =>
        currentTenant.value?.name || 'No Tenant'
    )

    // Actions
    const loadTenant = async (tenantId) => {
        logger.info('TENANT_STORE', `Loading tenant ${tenantId}`)
        isLoading.value = true

        try {
            const response = await api.get(`/superadmin/tenants/${tenantId}`)
            currentTenant.value = response.data.tenant
            tenantConfig.value = response.data.config || {}
            branding.value = response.data.branding || {}

            logger.info('TENANT_STORE', 'Tenant loaded successfully', {
                tenantId: currentTenant.value.id,
                tenantName: currentTenant.value.name
            })

            return { success: true, tenant: currentTenant.value }
        } catch (error) {
            logger.error('TENANT_STORE', 'Failed to load tenant', { error: error.message })
            return { success: false, error: error.response?.data?.error || 'Failed to load tenant' }
        } finally {
            isLoading.value = false
        }
    }

    const switchTenant = async (tenantId) => {
        logger.info('TENANT_STORE', `Switching to tenant ${tenantId}`)

        try {
            const response = await api.post(`/superadmin/tenants/${tenantId}/switch`)
            currentTenant.value = response.data.tenant
            tenantConfig.value = response.data.config || {}
            branding.value = response.data.branding || {}

            // Store in localStorage for persistence
            localStorage.setItem('current_tenant_id', tenantId)

            logger.info('TENANT_STORE', 'Switched tenant successfully')
            return { success: true }
        } catch (error) {
            logger.error('TENANT_STORE', 'Failed to switch tenant', { error: error.message })
            return { success: false, error: error.response?.data?.error || 'Failed to switch tenant' }
        }
    }

    const createTenant = async (tenantData) => {
        logger.info('TENANT_STORE', 'Creating new tenant', { name: tenantData.name })
        isLoading.value = true

        try {
            const response = await api.post('/superadmin/tenants', tenantData)
            const newTenant = response.data.tenant

            allTenants.value.push(newTenant)

            logger.info('TENANT_STORE', 'Tenant created successfully', {
                tenantId: newTenant.id,
                tenantName: newTenant.name
            })

            return { success: true, tenant: newTenant }
        } catch (error) {
            logger.error('TENANT_STORE', 'Failed to create tenant', { error: error.message })
            return { success: false, error: error.response?.data?.error || 'Failed to create tenant' }
        } finally {
            isLoading.value = false
        }
    }

    const updateTenant = async (tenantId, updates) => {
        logger.info('TENANT_STORE', `Updating tenant ${tenantId}`)
        isLoading.value = true

        try {
            const response = await api.put(`/superadmin/tenants/${tenantId}`, updates)
            const updatedTenant = response.data.tenant

            // Update in allTenants array
            const index = allTenants.value.findIndex(t => t.id === tenantId)
            if (index !== -1) {
                allTenants.value[index] = updatedTenant
            }

            // Update current tenant if it's the one being updated
            if (currentTenant.value?.id === tenantId) {
                currentTenant.value = updatedTenant
            }

            logger.info('TENANT_STORE', 'Tenant updated successfully')
            return { success: true, tenant: updatedTenant }
        } catch (error) {
            logger.error('TENANT_STORE', 'Failed to update tenant', { error: error.message })
            return { success: false, error: error.response?.data?.error || 'Failed to update tenant' }
        } finally {
            isLoading.value = false
        }
    }

    const deleteTenant = async (tenantId) => {
        logger.info('TENANT_STORE', `Deleting tenant ${tenantId}`)
        isLoading.value = true

        try {
            await api.delete(`/superadmin/tenants/${tenantId}`)

            // Remove from allTenants array
            allTenants.value = allTenants.value.filter(t => t.id !== tenantId)

            // Clear current tenant if it was deleted
            if (currentTenant.value?.id === tenantId) {
                currentTenant.value = null
                tenantConfig.value = {}
                branding.value = {}
                localStorage.removeItem('current_tenant_id')
            }

            logger.info('TENANT_STORE', 'Tenant deleted successfully')
            return { success: true }
        } catch (error) {
            logger.error('TENANT_STORE', 'Failed to delete tenant', { error: error.message })
            return { success: false, error: error.response?.data?.error || 'Failed to delete tenant' }
        } finally {
            isLoading.value = false
        }
    }

    const fetchAllTenants = async () => {
        logger.info('TENANT_STORE', 'Fetching all tenants')
        isLoading.value = true

        try {
            const response = await api.get('/superadmin/tenants')
            allTenants.value = response.data.tenants || []

            logger.info('TENANT_STORE', `Fetched ${allTenants.value.length} tenants`)
            return { success: true, tenants: allTenants.value }
        } catch (error) {
            logger.error('TENANT_STORE', 'Failed to fetch tenants', { error: error.message })
            return { success: false, error: error.response?.data?.error || 'Failed to fetch tenants' }
        } finally {
            isLoading.value = false
        }
    }

    const updateBranding = async (tenantId, brandingData) => {
        logger.info('TENANT_STORE', `Updating branding for tenant ${tenantId}`)

        try {
            const response = await api.put(`/superadmin/tenants/${tenantId}/branding`, brandingData)

            if (currentTenant.value?.id === tenantId) {
                branding.value = response.data.branding
            }

            logger.info('TENANT_STORE', 'Branding updated successfully')
            return { success: true, branding: response.data.branding }
        } catch (error) {
            logger.error('TENANT_STORE', 'Failed to update branding', { error: error.message })
            return { success: false, error: error.response?.data?.error || 'Failed to update branding' }
        }
    }

    // Initialize - load tenant from localStorage if exists
    const initTenant = () => {
        const tenantId = localStorage.getItem('current_tenant_id')
        if (tenantId) {
            loadTenant(tenantId)
        }
    }

    return {
        // State
        currentTenant,
        tenantConfig,
        branding,
        allTenants,
        isLoading,

        // Getters
        activeTenants,
        suspendedTenants,
        trialTenants,
        hasTenant,
        tenantName,

        // Actions
        loadTenant,
        switchTenant,
        createTenant,
        updateTenant,
        deleteTenant,
        fetchAllTenants,
        updateBranding,
        initTenant
    }
})